import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Target, 
  Users, 
  Lightbulb, 
  TrendingUp,
  Globe,
  Cpu,
  Workflow,
  BarChart3,
  ArrowRight,
  Sparkles
} from "lucide-react";
import SEOHead from "@/components/SEOHead";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_data-xm-dev/artifacts/nfhnqfv8_640AB213-1DF7-4C40-9AAA-5ECCBB333955.png";

const specializations = [
  { icon: Globe, text: "Professional websites", color: "from-blue-500 to-cyan-500" },
  { icon: Workflow, text: "Custom CRMs and workflow platforms", color: "from-violet-500 to-purple-500" },
  { icon: Cpu, text: "Automation tools", color: "from-blue-600 to-violet-600" },
  { icon: BarChart3, text: "AI integrations", color: "from-purple-500 to-pink-500" },
  { icon: TrendingUp, text: "Digital infrastructure", color: "from-cyan-500 to-blue-500" },
];

const values = [
  {
    icon: Target,
    title: "Mission-Driven",
    description: "Every solution we build is designed with your business goals in mind.",
    color: "from-blue-500 to-cyan-500",
  },
  {
    icon: Users,
    title: "Client-Focused",
    description: "We work closely with our clients to understand their unique challenges.",
    color: "from-violet-500 to-purple-500",
  },
  {
    icon: Lightbulb,
    title: "Innovation First",
    description: "We leverage the latest technologies to deliver cutting-edge solutions.",
    color: "from-blue-600 to-violet-600",
  },
  {
    icon: TrendingUp,
    title: "Growth Oriented",
    description: "Our solutions are built to scale with your business.",
    color: "from-purple-500 to-pink-500",
  },
];

const stats = [
  { value: "100+", label: "Projects Delivered" },
  { value: "50+", label: "Happy Clients" },
  { value: "5+", label: "Years Experience" },
  { value: "24/7", label: "Support Available" },
];

export const AboutPage = () => {
  return (
    <>
      <SEOHead 
        title="About Us | Global Data XM - Digital Solutions Company"
        description="Global Data XM is a digital solutions company focused on building modern websites, workflow systems, and AI-powered business tools."
      />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-slate-900 relative overflow-hidden" data-testid="about-hero">
        {/* Background elements */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900" />
        <div className="absolute top-20 left-10 w-96 h-96 bg-blue-600/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-violet-600/10 rounded-full blur-[120px]" />
        
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/10 rounded-full px-4 py-2 mb-6"
            >
              <Sparkles size={16} className="text-blue-400" />
              <span className="text-sm font-medium text-slate-300">About Us</span>
            </motion.div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              About{" "}
              <span className="bg-gradient-to-r from-blue-400 via-violet-400 to-purple-400 bg-clip-text text-transparent">
                Global Data XM
              </span>
            </h1>
            <p className="text-lg text-slate-400 leading-relaxed">
              A digital solutions company bridging the gap between human creativity and AI efficiency.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-slate-900" data-testid="about-content">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Building Digital Solutions for the Modern Era
              </h2>
              <div className="space-y-4 text-slate-400 leading-relaxed">
                <p>
                  Global Data XM is a digital solutions company focused on building modern websites, workflow systems, and AI-powered business tools.
                </p>
                <p>
                  Our mission is to help businesses modernize their operations through technology that is scalable, efficient, and designed for growth.
                </p>
                <p>
                  The company combines modern design, automation, and development to create systems that improve efficiency and increase productivity.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              {/* Glow effect */}
              <div className="absolute inset-0 bg-blue-500/20 blur-[60px]" />
              
              <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-3xl p-2 border border-white/10">
                <div className="bg-slate-800/50 rounded-2xl p-8 flex items-center justify-center min-h-[400px]">
                  <motion.img 
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    src={LOGO_URL}
                    alt="Global Data XM"
                    className="w-64 h-auto drop-shadow-2xl"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-slate-800/50" data-testid="stats">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/10 hover:border-white/20 transition-colors"
              >
                <p className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-400 to-violet-400 bg-clip-text text-transparent">
                  {stat.value}
                </p>
                <p className="text-slate-200 text-sm mt-2">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Specializations */}
      <section className="py-20 bg-slate-900" data-testid="specializations">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-sm font-medium text-violet-400 uppercase tracking-wider">Expertise</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-3">
              We Specialize In
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {specializations.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-white/20 hover:bg-white/10 transition-all text-center"
                data-testid={`specialization-${index}`}
              >
                <div className={`w-14 h-14 bg-gradient-to-r ${item.color} rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                  <item.icon size={26} className="text-white" />
                </div>
                <p className="text-white font-medium text-sm">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-slate-800/50" data-testid="values">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-sm font-medium text-blue-400 uppercase tracking-wider">Our Values</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-3">
              What Drives Us
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-white/20 hover:bg-white/10 transition-all"
                data-testid={`value-${index}`}
              >
                <div className={`w-14 h-14 bg-gradient-to-r ${value.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <value.icon size={26} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{value.title}</h3>
                <p className="text-slate-200 text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-r from-blue-600 via-violet-600 to-purple-600 relative overflow-hidden" data-testid="about-cta">
        {/* Animated background */}
        <div className="absolute inset-0">
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 8, repeat: Infinity }}
            className="absolute top-0 left-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl"
          />
          <motion.div
            animate={{ scale: [1.2, 1, 1.2], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 10, repeat: Infinity }}
            className="absolute bottom-0 right-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl"
          />
        </div>
        
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Ready to Work Together?
            </h2>
            <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
              Let's discuss how we can help transform your business with modern technology.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-full font-medium hover:shadow-xl transition-all hover:scale-105"
              data-testid="about-cta-btn"
            >
              Start a Project
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
